import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Save,
  Users,
  Globe,
  Image as ImageIcon, // Renamed to avoid conflict if needed, though simpler is nice
  Building,
  LayoutTemplate
} from 'lucide-react';
import { ImageUpload } from '@/components/ui/image-upload';
import { clients } from '@/data/projects';
import { useClients, useCreateClient, useUpdateClient, useClient } from '@/hooks/use-clients';
import { useUpload } from '@/hooks/use-upload';
import { useToast } from '@/hooks/use-toast';

const clientSchema = z.object({
  name: z.string().min(1, 'Nome do cliente é obrigatório'),
  logo: z.string().min(1, 'Logo é obrigatório'),
  url: z.string().url('URL deve ser válida').optional().or(z.literal('')),
  description: z.string().optional(),
});

type ClientFormData = z.infer<typeof clientSchema>;

const ClienteForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { uploadImage, base64ToFile } = useUpload();
  const isEditing = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [hasWebsite, setHasWebsite] = useState(false);

  // Fetch client if editing using the hook
  const { data: client, isLoading: isClientLoading } = useClient(id || '');

  const createClient = useCreateClient();
  const updateClient = useUpdateClient();

  // IMPORTANT: All hooks must be called before any conditional returns
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset
  } = useForm<ClientFormData>({
    resolver: zodResolver(clientSchema),
    defaultValues: {
      name: '',
      logo: '',
      url: '',
      description: '',
    },
  });

  const watchedValues = watch();

  // Update form when data loads
  useEffect(() => {
    if (client) {
      reset({
        name: client.name,
        logo: client.logo,
        url: client.url || '',
        description: client.description || '',
      });
      setHasWebsite(Boolean(client.url));
    }
  }, [client, reset]);

  useEffect(() => {
    setHasWebsite(Boolean(watchedValues.url));
  }, [watchedValues.url]);

  // Handle loading state for initial data fetch (AFTER all hooks)
  if (isEditing && isClientLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando dados do cliente...</p>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: ClientFormData) => {
    setIsLoading(true);
    try {
      console.log('Submitting data:', data); // Log the raw form data

      let logoUrl = data.logo;

      // Se o logo for base64, fazer upload antes de salvar
      if (data.logo.startsWith('data:')) {
        const file = await base64ToFile(data.logo, `client-logo-${Date.now()}.png`);
        const uploadedUrl = await uploadImage(file, 'images'); // Using 'images' bucket

        if (!uploadedUrl) {
          throw new Error("Falha ao fazer upload da imagem.");
        }
        logoUrl = uploadedUrl;
      }

      // Ensure specific fields match the API expectation
      const finalData = {
        name: data.name,
        logo: logoUrl,
        // Send null if no website, or the url string if it exists
        // Supabase treats undefined as "do not change" in updates, but for inserts we want explicit null usually if it's nullable
        url: hasWebsite && data.url ? data.url : null,
        description: data.description || null // prefer null over empty string for optional text
      };

      console.log('Final payload to API:', finalData);

      if (isEditing && id) {
        // For updates, we pass undefined for fields that shouldn't change if we were doing partial updates, 
        // but here we are replacing/updating specific fields from the form.
        // Cast to any to satisfy the partial type if needed, or just let typescript handle it if Client allows null | string
        await updateClient.mutateAsync({ id, updates: finalData as any });
        toast({ title: "Sucesso", description: "Cliente atualizado com sucesso" });
      } else {
        await createClient.mutateAsync(finalData as any);
        toast({ title: "Sucesso", description: "Cliente criado com sucesso" });
      }

      navigate('/admin/clientes');
    } catch (error: any) {
      console.error('Erro detalhado ao salvar cliente:', error);

      // Try to extract a meaningful message
      const errorMessage = error.message || error.error_description || "Falha ao salvar cliente. Verifique o console.";

      toast({
        title: "Erro ao salvar",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {/* Premium Sticky Header */}
      <div className="sticky top-0 z-20 bg-background/80 backdrop-blur-md pb-4 pt-2 border-b mb-6 -mx-6 px-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/clientes')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEditing ? 'Editar Cliente' : 'Novo Cliente'}
            </h1>
            <p className="text-muted-foreground hidden md:block">
              {isEditing ? 'Atualize as informações do cliente' : 'Adicione um novo cliente ao portfólio'}
            </p>
          </div>
        </div>
        <Button onClick={handleSubmit(onSubmit)} disabled={isLoading} className="shadow-lg bg-blue-600 hover:bg-blue-700 transition-all">
          <Save className="w-4 h-4 mr-2" />
          {isLoading ? 'Salvando...' : 'Salvar Cliente'}
        </Button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left Column */}
          <div className="lg:col-span-2 space-y-6">

            {/* Basic Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Building className="w-5 h-5 text-blue-500" />
                  <span>Informações Básicas</span>
                </CardTitle>
                <CardDescription>
                  Dados principais da empresa parceira
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Cliente *</Label>
                  <Input
                    id="name"
                    {...register('name')}
                    placeholder="Ex: Stone, Nova Era, TechCorp"
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive">{errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrição (opcional)</Label>
                  <Textarea
                    id="description"
                    {...register('description')}
                    placeholder="Breve descrição da empresa, setor de atuação ou informações relevantes..."
                    rows={4}
                  />
                  <p className="text-sm text-muted-foreground">
                    Esta descrição auxilia na identificação interna e contexto
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Website Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Globe className="w-5 h-5 text-blue-500" />
                  <span>Presença Digital</span>
                </CardTitle>
                <CardDescription>
                  Website e links externos do cliente
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-2 p-4 border rounded-lg bg-muted/50">
                  <Checkbox
                    id="hasWebsite"
                    checked={hasWebsite}
                    onCheckedChange={(checked) => setHasWebsite(checked as boolean)}
                  />
                  <Label htmlFor="hasWebsite" className="cursor-pointer">
                    Cliente possui website oficial
                  </Label>
                </div>

                {hasWebsite && (
                  <div className="space-y-2 pt-2">
                    <Label htmlFor="url">URL do Website</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="url"
                        {...register('url')}
                        placeholder="https://www.exemplo.com.br"
                        className="pl-10"
                      />
                    </div>
                    {errors.url && (
                      <p className="text-sm text-destructive">{errors.url.message}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Right Column */}
          <div className="space-y-6">

            {/* Logo Upload Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <ImageIcon className="w-5 h-5 text-blue-500" />
                  <span>Logo da Empresa</span>
                </CardTitle>
                <CardDescription>
                  Identidade visual
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ImageUpload
                  label="Upload do Logo *"
                  value={watch('logo')}
                  onChange={(value) => {
                    setValue('logo', value, { shouldValidate: true });
                  }}
                />
                {errors.logo && (
                  <p className="text-sm text-destructive">{errors.logo.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Formato recomendado: PNG transparente (500x500px)
                </p>
              </CardContent>
            </Card>

            {/* Preview Card */}
            <Card className="bg-slate-950 text-white border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-white">
                  <LayoutTemplate className="w-5 h-5 text-blue-400" />
                  <span>Live Preview</span>
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Visualização no grid de clientes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-900 rounded-lg p-6 flex flex-col items-center justify-center border border-slate-800 space-y-4">
                  <div className="w-full aspect-video flex items-center justify-center p-4 bg-slate-950/50 rounded-md border border-slate-800/50">
                    {watchedValues.logo ? (
                      <img
                        src={watchedValues.logo}
                        alt={watchedValues.name || 'Client Logo'}
                        className="max-w-[120px] max-h-[80px] object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                      />
                    ) : (
                      <div className="text-slate-600 text-sm">Sem logo</div>
                    )}
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-lg text-slate-200">
                      {watchedValues.name || 'Nome do Cliente'}
                    </p>
                    {hasWebsite && watchedValues.url && (
                      <p className="text-xs text-blue-400 mt-1 flex items-center justify-center gap-1">
                        <Globe className="w-3 h-3" />
                        Website Link
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-4 text-xs text-center text-slate-500">
                  Simulação de visualização na seção "Sobre"
                </div>
              </CardContent>
            </Card>

            {/* Status Info */}
            <Card>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Status</p>
                    <p className="text-sm font-semibold text-green-600 flex items-center justify-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span> Ativo
                    </p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg text-center">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Cadastro</p>
                    <p className="text-sm text-foreground">
                      {new Date().toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </form>
    </div>
  );
};

export default ClienteForm;
